import { useCallback, type Dispatch } from 'react';
import { atom, useAtom } from 'jotai';
import { COLOR_PALETTES, type ColorPaletteName } from '../../constants/color';
import { toRgb, type HexColor, type RgbColor } from '../../utils/color';

const mapColorPaletteAtom = atom<ColorPaletteName>('vivid');

const mapColorOpacityAtom = atom<number>(0.5);

type UseMapColorPalette = {
  mapColorPalette: ColorPaletteName;
  mapColorOpacity: number;
  getHexColors: (
    count: number | 'all',
    paletteName?: ColorPaletteName
  ) => HexColor[];
  getRgbColors: (
    count: number | 'all',
    paletteName?: ColorPaletteName
  ) => RgbColor[];
  setMapColorPalette: Dispatch<ColorPaletteName>;
  setMapColorOpacity: Dispatch<number>;
};

const useMapColorPalette = (): UseMapColorPalette => {
  const [mapColorPalette, setMapColorPalette] = useAtom(mapColorPaletteAtom);
  const [mapColorOpacity, setMapColorOpacity] = useAtom(mapColorOpacityAtom);

  const getHexColors = useCallback(
    (count: number | 'all', paletteName?: ColorPaletteName): HexColor[] => {
      const palette = COLOR_PALETTES[paletteName ?? mapColorPalette];

      if (count === 'all') {
        return palette;
      }
      if (palette.length < count) {
        throw new Error(
          `Too many colors [${count}] requested for the palette.`
        );
      }
      // 指定された数だけ、パレット全体から均等になるように色を選んで返す
      // 例えば8色の中から3色を選ぶ場合、0番目、3番目、7番目の色が選ばれるようにする
      return Array.from({ length: count }, (_, index: number): HexColor => {
        const ratio = count > 1 ? index / (count - 1) : 1;
        const colorIndex = Math.round(ratio * (palette.length - 1));
        return palette[colorIndex];
      });
    },
    [mapColorPalette]
  );

  const getRgbColors = useCallback(
    (count: number | 'all', paletteName?: ColorPaletteName): RgbColor[] => {
      const palette = COLOR_PALETTES[paletteName ?? mapColorPalette];

      if (count === 'all') {
        return palette.map(toRgb);
      }
      if (palette.length < count) {
        throw new Error(
          `Too many colors [${count}] requested for the palette.`
        );
      }
      // 指定された数だけ、パレット全体から均等になるように色を選んで返す
      // 例えば8色の中から3色を選ぶ場合、0番目、3番目、7番目の色が選ばれるようにする
      return Array.from({ length: count }, (_, index: number): RgbColor => {
        const ratio = count > 1 ? index / (count - 1) : 1;
        const colorIndex = Math.round(ratio * (palette.length - 1));
        return toRgb(palette[colorIndex]);
      });
    },
    [mapColorPalette]
  );

  return {
    mapColorPalette,
    mapColorOpacity,
    getHexColors,
    getRgbColors,
    setMapColorPalette,
    setMapColorOpacity,
  };
};

export { useMapColorPalette };
